/**
 * Utilities for parsing and handling [[IMG:n]] markers in answer text.
 */

export interface ImageMarker {
  fullMatch: string;
  index: number;
  pageNumber?: number;
  position: number;
}

/**
 * Parse image markers from text.
 * Markers: [[IMG:index]] or [[IMG:index:pN]]
 */
export function parseImageMarkers(text: string): ImageMarker[] {
  const regex = /\[\[IMG:(\d+)(?::p(\d+))?\]\]/g;
  const markers: ImageMarker[] = [];
  let match;

  while ((match = regex.exec(text)) !== null) {
    markers.push({
      fullMatch: match[0],
      index: parseInt(match[1], 10),
      pageNumber: match[2] ? parseInt(match[2], 10) : undefined,
      position: match.index,
    });
  }

  return markers;
}

/**
 * Strip image markers from text for display purposes.
 */
export function stripImageMarkers(text: string): string {
  return text
    .replace(/\[\[IMG:\d+(?::p\d+)?\]\]/g, "")
    .replace(/\s+/g, " ")
    .trim();
}

export type TextSegment = { type: "text"; content: string };
export type ImageSegment = { type: "image"; index: number };
export type AnswerSegment = TextSegment | ImageSegment;

/**
 * Parse answer text into segments of text and image markers.
 */
export function parseAnswerIntoSegments(text: string): AnswerSegment[] {
  const result: AnswerSegment[] = [];
  const regex = /\[\[IMG:(\d+)(?::p\d+)?\]\]/g;
  let lastIndex = 0;
  let match;

  while ((match = regex.exec(text)) !== null) {
    // Add text before marker
    if (match.index > lastIndex) {
      result.push({
        type: "text",
        content: text.slice(lastIndex, match.index),
      });
    }

    // Add image marker
    result.push({
      type: "image",
      index: parseInt(match[1], 10),
    });

    lastIndex = regex.lastIndex;
  }

  // Add remaining text
  if (lastIndex < text.length) {
    result.push({
      type: "text",
      content: text.slice(lastIndex),
    });
  }

  return result;
}
